import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import PostGenerator from './PostGenerator';
import PostPreview from './PostPreview';
import PostHistory from './PostHistory';
import AdminPanel from './AdminPanel';
import ReferralPanel from './ReferralPanel';
import AnalyticsPanel from './AnalyticsPanel';
import CalendarPanel from './CalendarPanel';
import NotificationCenter from './NotificationCenter';
import ProfileSettings from './ProfileSettings';
import BadgesPanel from './BadgesPanel';
import axios from 'axios';
import { HardHat, SignOut, PencilLine, ClockCounterClockwise, User, CreditCard, Crown, Gear, UserPlus, ChartBar, CalendarBlank, Trophy } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard({ onSubscribe }) {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('generator');
  const [latestPost, setLatestPost] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);
  const [usage, setUsage] = useState(null);

  const fetchUsage = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/posts/usage`, { withCredentials: true });
      setUsage(data);
    } catch (error) {
      console.error("Usage fetch error:", error.message);
    }
  }, []);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  const handlePostGenerated = useCallback((post) => {
    setLatestPost(post);
    setHistoryKey(prev => prev + 1);
    if (post.usage) setUsage(post.usage);
    else fetchUsage();
  }, [fetchUsage]);

  const isPro = user?.subscription_status === 'active';
  const isAdmin = user?.role === 'admin';

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="dashboard">
      <header className="bg-[#09090B] border-b border-[#27272A] sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#8B5CF6] flex items-center justify-center">
              <HardHat size={18} weight="duotone" className="text-white" />
            </div>
            <span className="font-bold text-lg tracking-tight text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} data-testid="app-title">Shanga Pro</span>
            {isPro && (
              <span className="hidden sm:flex items-center gap-1 text-xs font-bold text-[#A78BFA] bg-[#8B5CF6]/15 px-2 py-0.5 rounded-full">
                <Crown size={12} weight="fill" /> PRO
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <NotificationCenter onNavigate={(view) => { if (view === 'subscription') onSubscribe(); else setActiveTab(view); }} />
            {!isPro && (
              <button onClick={onSubscribe} data-testid="upgrade-btn"
                className="h-9 px-3 text-xs bg-[#8B5CF6] text-white rounded-lg font-bold hover:bg-[#7C3AED] transition-colors flex items-center gap-1.5">
                <CreditCard size={14} weight="duotone" /> Passer Pro
              </button>
            )}
            <span className="text-sm text-[#A1A1AA] hidden sm:flex items-center gap-1.5">
              <User size={16} weight="duotone" /> {user?.name || user?.email}
            </span>
            <button onClick={logout} data-testid="logout-button"
              className="h-9 px-3 text-sm border border-[#27272A] rounded-lg hover:bg-[#18181B] flex items-center gap-1.5 text-[#A1A1AA] hover:text-[#F4F4F5] transition-colors">
              <SignOut size={16} weight="duotone" /><span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex gap-1 mb-6 bg-[#18181B] border border-[#27272A] rounded-lg p-1 w-fit overflow-x-auto">
          {[
            { id: 'generator', icon: PencilLine, label: 'Générateur' },
            { id: 'history', icon: ClockCounterClockwise, label: 'Historique' },
            { id: 'referral', icon: UserPlus, label: 'Parrainage' },
            { id: 'analytics', icon: ChartBar, label: 'Stats' },
            { id: 'calendar', icon: CalendarBlank, label: 'Calendrier' },
            { id: 'badges', icon: Trophy, label: 'Badges' },
            { id: 'profile', icon: User, label: 'Profil' },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
              className={`h-10 px-4 rounded-md text-sm font-medium flex items-center gap-2 transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-[#8B5CF6] text-white' : 'text-[#A1A1AA] hover:text-[#F4F4F5]'}`}>
              <tab.icon size={16} weight="duotone" /> {tab.label}
            </button>
          ))}
          {isAdmin && (
            <button onClick={() => setActiveTab('admin')} data-testid="tab-admin"
              className={`h-10 px-4 rounded-md text-sm font-medium flex items-center gap-2 transition-all whitespace-nowrap ${activeTab === 'admin' ? 'bg-[#8B5CF6] text-white' : 'text-[#A1A1AA] hover:text-[#F4F4F5]'}`}>
              <Gear size={16} weight="duotone" /> Admin
            </button>
          )}
        </div>
        {activeTab === 'generator' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-5 sm:p-6">
                <h2 className="text-xl font-bold tracking-tight mb-1 text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Nouveau post</h2>
                <p className="text-sm text-[#A1A1AA] mb-5">Remplissez les détails pour générer votre post marketing</p>
                <PostGenerator onPostGenerated={handlePostGenerated} usage={usage} onSubscribe={onSubscribe} />
              </div>
            </div>
            <div>
              <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-[#71717A] mb-3">Aperçu du post</h3>
              {latestPost ? <PostPreview post={latestPost} /> : (
                <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-8 text-center animate-fade-in">
                  <img src="https://images.unsplash.com/photo-1759677956964-e1f8a791c956?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHx0cm9waWNhbCUyMGFyY2hpdGVjdHVyZSUyMGV4dGVyaW9yfGVufDB8fHx8MTc3NjEzMDA2Nnww&ixlib=rb-4.1.0&q=85"
                    alt="Placeholder" className="w-24 h-24 mx-auto rounded-lg object-cover mb-4 opacity-40" />
                  <p className="text-sm text-[#A1A1AA]">Votre post apparaîtra ici</p>
                  <p className="text-xs text-[#71717A] mt-1">Remplissez le formulaire et cliquez sur "Générer"</p>
                </div>
              )}
            </div>
          </div>
        )}
        {activeTab === 'history' && <PostHistory key={historyKey} />}
        {activeTab === 'referral' && <ReferralPanel />}
        {activeTab === 'analytics' && <AnalyticsPanel />}
        {activeTab === 'calendar' && <CalendarPanel />}
        {activeTab === 'badges' && <BadgesPanel />}
        {activeTab === 'profile' && <ProfileSettings />}
        {activeTab === 'admin' && isAdmin && <AdminPanel />}
      </main>
    </div>
  );
}
